import { lazy, Suspense, type FC } from 'react';

import { FormattedPost, Post, SocialChannels } from '@/types';
import { cn, renderHtmlContent } from '@/lib/utils';

import './PostSection.css';

// Preload social media components
const FacebookBox = lazy(() =>
  import(
    '@/features/Post/FormattedPost/components/SocialFormattedBox/FacebookBox'
  ).then((module) => ({ default: module.default }))
);
const InstagramBox = lazy(() =>
  import(
    '@/features/Post/FormattedPost/components/SocialFormattedBox/InstagramBox'
  ).then((module) => ({ default: module.default }))
);
const LinkedInBox = lazy(() =>
  import(
    '@/features/Post/FormattedPost/components/SocialFormattedBox/LinkedInBox'
  ).then((module) => ({ default: module.default }))
);

interface PostSectionProps {
  projectId: string;
  projectName?: string;
  post: Post | FormattedPost;
  isFormatted?: boolean;
  currentFormat?: SocialChannels;
  isRecent?: boolean;
  postClassName?: string;
  postId?: string;
}

const PostSection: FC<PostSectionProps> = ({
  post,
  projectName = 'user',
  isRecent = false,
  postClassName,
  currentFormat,
}) => {
  const { title, content_html } = post;
  const postImage = (post?.images?.length && post.images[0].url) || null;

  const renderSocialBox = () => {
    const props = {
      title,
      image: postImage || '',
      username: projectName,
      content: renderHtmlContent(content_html),
    };

    if (currentFormat === SocialChannels.Facebook) {
      return <FacebookBox {...props} />;
    }
    if (currentFormat === SocialChannels.LinkedIn) {
      return <LinkedInBox {...props} />;
    }
    if (currentFormat === SocialChannels.Instagram) {
      return <InstagramBox {...props} />;
    }

    return (
      <div className={'flex flex-col gap-6 pt-6 px-8'}>
        <h2 className={'font-bold'}>{title}</h2>

        {postImage && (
          <div className={'h-0 pb-[75%] relative'}>
            <div className='absolute inset-0 w-full mx-auto'>
              <img
                src={postImage}
                alt='Uploaded preview'
                className='size-full object-contain rounded-lg overflow-hidden'
              />
            </div>
          </div>
        )}

        <div
          className={cn('text-[12px] h-full flex flex-col gap-2 ', {
            'line-clamp': isRecent,
          })}
        >
          {renderHtmlContent(content_html)}
        </div>
      </div>
    );
  };

  return (
    <div
      key={`${post.title}-${currentFormat}`}
      className={cn(
        'bg-white rounded-xl border border-light-grey w-[300px] max-w-full mx-auto shadow-box-1 font-roboto overflow-hidden',
        'max-h-[calc(100vh-250px)] overflow-y-auto',
        postClassName,
        { 'break-inside-avoid max-2xl:min-w-full': isRecent },
        '[&_svg]:w-full text-sm leading-[1.3]'
      )}
    >
      <Suspense fallback={<div className='p-4'>Loading...</div>}>
        <div>{renderSocialBox()}</div>
      </Suspense>
    </div>
  );
};

export default PostSection;
